import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router, Switch } from 'react-router-dom';
import { DragDropContextProvider } from 'react-dnd7';
import HTML5Backend from 'react-dnd-html5-backend7';
import { ApiDataStore } from './contexts/ApiDataContext';
import { AppStore } from './contexts/AppContext';
import { AuthenticationStore } from './contexts/AuthenticationContext';
import { AppDivisionProvider } from './contexts/AppDivisionContext';
import { RolesStore } from './contexts/RolesContext';
import groupedRoutes from './routes';
import { env } from './config';
import { TenantFeatureContext } from './contexts/TenantContext';
// import { useMatomo } from "@datapunt/matomo-tracker-react";
import { DashboardProvider } from './contexts/DashboardContext';
import { AppLogoProvider } from './contexts/AppLogoContext';
import { OfflineProvider } from './contexts/OfflineContext';
// SW2-2458: progress/Retry notices for finding photos uploading in the background.
import BackgroundUploadsNotifier from './components/Shared/BackgroundUploads/BackgroundUploadsNotifier';
import OfflineAutoSync from './components/Shared/OfflineAutoSync/OfflineAutoSync';
// SW2-2394: sends context + recent failures as Usersnap custom data. Must sit
// inside the providers, which App itself renders.
import UsersnapCustomData, {
  USERSNAP_READY_EVENT,
} from './components/Shared/UsersnapCustomData';
// SW2-2394: records a recent-activity timeline (failed calls, successful
// mutations, uncaught JS errors) so a Usersnap report carries its own evidence.
// Stage has no trialRestrictionInterceptor yet (SW2-2066); when it arrives it
// must be imported AFTER this line — axios runs response interceptors in
// registration order and that one swallows some 403s. See activityBuffer.ts.
import './utils/activityBuffer';

const client = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // const { trackPageView } = useMatomo();

  useEffect(() => {
    // This is a reference to usersnapApi
    let api: any = null;

    // Please don't change the name otherwise disaster might fall upon you
    // @ts-ignore: Property 'onUsersnapCXLoad' does not exist on type 'Window & typeof globalThis'
    window.onUsersnapCXLoad = function (_api: any) {
      const initParams = {};

      api = _api;
      api.init(initParams);

      // Usersnap's own documented pattern. UsersnapCustomData needs the api
      // object and cannot reach this closure.
      // @ts-ignore: Property 'Usersnap' does not exist on type 'Window'
      window.Usersnap = _api;

      // Tell UsersnapCustomData immediately, rather than leaving it to notice on
      // its next poll — the widget is clickable as soon as this runs, and a
      // report opened before the handler attaches would carry no custom data.
      window.dispatchEvent(new Event(USERSNAP_READY_EVENT));
    };

    const title = document.createElement('title');

    const tenantName = window.location.host.split('.')[0];

    title.innerText = `${
      env.REACT_APP_DOCUMENT_TITLE
    } - ${tenantName.toUpperCase()}`;
    document.head.appendChild(title);

    let script: HTMLScriptElement;

    if (
      env.REACT_APP_FEATURE_USER_FEEDBACK_WIDGET === 'true' &&
      env.REACT_APP_USER_FEEDBACK_WIDGET_SCRIPT_SRC
    ) {
      script = document.createElement('script');

      script.defer = true;
      script.src = env.REACT_APP_USER_FEEDBACK_WIDGET_SCRIPT_SRC;

      document.body.appendChild(script);
    }

    return () => {
      if (api) {
        api.destroy();
      }
      if (script) {
        script.remove();
      }
    };
  }, []);

  // useEffect(() => {
  //   trackPageView({});
  // }, [trackPageView]);

  return (
    <QueryClientProvider client={client}>
      <OfflineProvider>
        <AppLogoProvider>
          <AppStore>
            <AuthenticationStore>
              {/** @todo wrap Router around _authorization_ context provider */}
              <RolesStore>
                <ApiDataStore>
                  <Router>
                    <AppDivisionProvider>
                      <DashboardProvider>
                        <TenantFeatureContext>
                          <BackgroundUploadsNotifier />
                          <OfflineAutoSync />
                          <UsersnapCustomData />
                          <DragDropContextProvider backend={HTML5Backend}>
                            <Switch>
                              {groupedRoutes.map(({ Layout, Route, routes }) =>
                                routes.map(({ path, Component, exact }) => (
                                  <Route
                                    path={path}
                                    Component={Component}
                                    Layout={Layout}
                                    exact={exact || false}
                                  />
                                )),
                              )}
                            </Switch>
                          </DragDropContextProvider>
                        </TenantFeatureContext>
                      </DashboardProvider>
                    </AppDivisionProvider>
                  </Router>
                </ApiDataStore>
              </RolesStore>
            </AuthenticationStore>
          </AppStore>
        </AppLogoProvider>
      </OfflineProvider>
    </QueryClientProvider>
  );
}

export default App;
